import { useState, useEffect } from 'react';
import { BenefitFiltersDto } from '@/services/platform/api/model';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Icon } from '@/components/ui/icon';
import { useClientTranslation } from '@/lib/core';

interface BenefitsFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: BenefitFiltersDto;
  activeCategories: string[];
  activeLocations: string[];
  onApply: (categories: string[], locations: string[]) => void;
  onClear: () => void;
}

export function BenefitsFilterSheet({
  open,
  onOpenChange,
  filters,
  activeCategories,
  activeLocations,
  onApply,
  onClear,
}: BenefitsFilterSheetProps) {
  const translation = useClientTranslation();

  const [draftCategories, setDraftCategories] = useState<string[]>(activeCategories);
  const [draftLocations, setDraftLocations] = useState<string[]>(activeLocations);

  useEffect(() => {
    if (open) {
      setDraftCategories(activeCategories);
      setDraftLocations(activeLocations);
    }
  }, [open, activeCategories, activeLocations]);

  const totalActive = draftCategories.length + draftLocations.length;

  function toggleCategory(id: string) {
    setDraftCategories(prev => (prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]));
  }

  function toggleLocation(id: string) {
    setDraftLocations(prev => (prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]));
  }

  function handleApply() {
    onApply(draftCategories, draftLocations);
    onOpenChange(false);
  }

  function handleClear() {
    setDraftCategories([]);
    setDraftLocations([]);
    onClear();
    onOpenChange(false);
  }

  const hasCategories = (filters.categories?.length ?? 0) > 0;
  const hasLocations = (filters.locations?.length ?? 0) > 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="flex max-h-[85dvh] flex-col overflow-hidden">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-2 py-3.75">
          <div className="w-11" />
          <p className="font-pp-neue-montreal text-[14px] leading-none font-normal tracking-[0.5px] text-[#1a1a1a] uppercase">
            {translation('App.Labels.FilterTitle')}
          </p>
          <button
            type="button"
            aria-label={translation('App.Labels.Close')}
            className="flex size-11 cursor-pointer items-center justify-center rounded-full"
            onClick={() => onOpenChange(false)}
          >
            <Icon name="xmark" width={24} height={24} className="text-[#1a1a1a]" />
          </button>
        </div>

        {/* Accordion filters */}
        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto px-4 pb-16">
          <Accordion type="multiple" className="border-b-[0.5px] border-[#4e4e4e]">
            {hasCategories && (
              <AccordionItem value="category">
                <AccordionTrigger>{translation('App.Labels.Category')}</AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-3 pb-4">
                    {filters.categories!.map(cat => (
                      <label key={cat.id} className="flex cursor-pointer items-center gap-3">
                        <Checkbox
                          checked={draftCategories.includes(cat.id ?? '')}
                          onCheckedChange={() => toggleCategory(cat.id ?? '')}
                        />
                        <span className="font-pp-neue-montreal text-[14px] text-[#1a1a1a]">{cat.name}</span>
                      </label>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {hasLocations && (
              <AccordionItem value="location">
                <AccordionTrigger>{translation('App.Labels.Location')}</AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-3 pb-4">
                    {filters.locations!.map(loc => (
                      <label key={loc.id} className="flex cursor-pointer items-center gap-3">
                        <Checkbox
                          checked={draftLocations.includes(loc.id ?? '')}
                          onCheckedChange={() => toggleLocation(loc.id ?? '')}
                        />
                        <span className="font-pp-neue-montreal text-[14px] text-[#1a1a1a]">{loc.name}</span>
                      </label>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>

        {/* Footer */}
        <div
          className="shrink-0 border-t-[0.5px] border-[#c8c8c8] px-4 pt-6 pb-10.5"
          style={{ display: 'flex', flexDirection: 'column', gap: totalActive > 0 ? '8px' : '4px' }}
        >
          <button
            type="button"
            disabled={totalActive === 0}
            className={`font-pp-neue-montreal h-11.5 w-full rounded-[2px] text-[12px] font-semibold tracking-[0.5px] uppercase transition-colors ${
              totalActive > 0 ? 'bg-[#1a1a1a] text-white' : 'border-[0.5px] border-[#666] text-[#a9a9a9]'
            }`}
            onClick={handleApply}
          >
            {translation('App.Labels.FilterShowResults')}
          </button>
          <button
            type="button"
            className={`font-pp-neue-montreal h-9 w-full text-[12px] font-normal tracking-[0.5px] uppercase transition-colors ${
              totalActive > 0 ? 'text-[#1a1a1a]' : 'text-[#848484]'
            }`}
            onClick={handleClear}
          >
            {translation('App.Labels.FilterClearAll')}
            {totalActive > 0 && ` (${totalActive})`}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
